import { Head, Link, useForm } from "@inertiajs/react"
import { AlertCircle, ArrowLeft, Check, Clock, X } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { adminProfileClaimsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Claim {
  id: number
  developer_name: string
  developer_id: number
  email: string
  message: string
  status: string
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
}

interface Props {
  claims: Claim[]
}

export default function AdminProfileClaimsIndex({ claims }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Admin", href: "/admin" },
    { title: "Profile Claims", href: adminProfileClaimsPath() },
  ]

  const approveForm = useForm({})
  const rejectForm = useForm({})

  const handleApprove = (claimId: number) => {
    approveForm.post(`/admin/profile_claims/${claimId}/approve`)
  }

  const handleReject = (claimId: number) => {
    rejectForm.post(`/admin/profile_claims/${claimId}/reject`)
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      pending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
    }

    const icons = {
      pending: <Clock className="h-3 w-3" />,
      approved: <Check className="h-3 w-3" />,
      rejected: <X className="h-3 w-3" />,
    }

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
          styles[status as keyof typeof styles]
        }`}
      >
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Profile Claims - Admin" />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              href="/"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Profile Claims</h1>
              <p className="text-muted-foreground">
                Review and manage profile claim requests
              </p>
            </div>
          </div>
        </div>

        {claims.length === 0 ? (
          <div className="rounded-xl border p-8 text-center">
            <AlertCircle className="text-muted-foreground mx-auto h-12 w-12" />
            <h3 className="mt-4 text-lg font-semibold">No claims yet</h3>
            <p className="text-muted-foreground mt-2">
              When developers submit profile claims, they will appear here.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border">
            <div className="divide-y">
              {claims.map((claim) => (
                <div key={claim.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Link
                          href={`/rankings/${claim.developer_id}`}
                          className="font-semibold hover:underline"
                        >
                          {claim.developer_name}
                        </Link>
                        {getStatusBadge(claim.status)}
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {claim.email}
                      </p>
                      <div className="bg-muted/50 mt-3 rounded-lg p-3">
                        <p className="text-sm">{claim.message}</p>
                      </div>
                      <div className="text-muted-foreground mt-3 flex gap-4 text-xs">
                        <span>
                          Submitted:{" "}
                          {new Date(claim.created_at).toLocaleDateString()}
                        </span>
                        {claim.reviewed_at && (
                          <>
                            <span>
                              Reviewed:{" "}
                              {new Date(claim.reviewed_at).toLocaleDateString()}
                            </span>
                            <span>by {claim.reviewed_by}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {claim.status === "pending" && (
                      <div className="ml-4 flex gap-2">
                        <button
                          onClick={() => handleApprove(claim.id)}
                          disabled={approveForm.processing}
                          className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="h-4 w-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(claim.id)}
                          disabled={rejectForm.processing}
                          className="bg-destructive hover:bg-destructive/90 text-destructive-foreground inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50"
                        >
                          <X className="h-4 w-4" />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
